/**
 * <read-more-text>
 *
 * Collapses its content to a number of lines (set via the `--max-lines` custom
 * property on the host) with a fade mask, and reveals a "read more" toggle only
 * when the content actually overflows.
 *
 * Markup contract (see snippets/cart-b2b-delivery-terms.liquid):
 *   <read-more-text style="--max-lines: 2;">
 *     <div data-read-more-content class="read-more-text__content read-more-text__content--collapsed">…</div>
 *     <button data-read-more-toggle data-read-more="…" data-read-less="…">…</button>
 *   </read-more-text>
 *
 * Loaded from a theme asset (not an inline snippet script) so the element is
 * registered even when its markup is injected into the cart drawer via a section
 * morph — inline <script> tags inserted by morph/innerHTML never execute.
 *
 * A ResizeObserver re-runs the overflow check when the element gains layout
 * (e.g. the drawer opens) and on later reflow.
 */
class ReadMoreText extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-read-more-content]');
    this.toggle = this.querySelector('[data-read-more-toggle]');
    if (!this.content || !this.toggle) return;

    this.readMoreText = this.toggle.dataset.readMore || 'Read more';
    this.readLessText = this.toggle.dataset.readLess || 'Read less';

    this.onToggle = this.onToggle.bind(this);
    this.checkOverflow = this.checkOverflow.bind(this);

    this.toggle.addEventListener('click', this.onToggle);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.checkOverflow);
      this.resizeObserver.observe(this.content);
    } else {
      window.addEventListener('resize', this.checkOverflow);
    }

    this.checkOverflow();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.checkOverflow);
    }
  }

  disconnectedCallback() {
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.checkOverflow);
  }

  checkOverflow() {
    if (!this.content || !this.toggle) return;
    const collapsed = this.content.classList.contains('read-more-text__content--collapsed');
    if (!collapsed) {
      this.toggle.classList.add('read-more-text__toggle--visible');
      return;
    }
    // Skip while the element has no layout (e.g. drawer still closed).
    if (this.content.clientHeight === 0) return;
    const overflowing = this.content.scrollHeight > this.content.clientHeight + 1;
    this.toggle.classList.toggle('read-more-text__toggle--visible', overflowing);
  }

  onToggle(event) {
    event.preventDefault();
    if (!this.content || !this.toggle) return;
    const collapsed = this.content.classList.toggle('read-more-text__content--collapsed');
    this.toggle.textContent = collapsed ? this.readMoreText : this.readLessText;
    this.toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  }
}

if (!customElements.get('read-more-text')) {
  customElements.define('read-more-text', ReadMoreText);
}
